import { useState } from 'react';
import Alert from '@mui/material/Alert';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ClientActivitySchema,
  ClientContactSchema,
  type ClientActivityDto,
  type EmployeeDto,
  type ProjectDto,
} from '@taqon/contracts';
import { getPage, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog } from '../../shared/ui/forms';
import { businessToday } from '../finance/line-editor';
import {
  ACTIVITIES,
  ActiveEmployeePicker,
  ActiveProjectPicker,
  CLIENTS,
  ListSelect,
  useCrmLists,
} from './common';

interface Values {
  activityTypeCode: string;
  activityDate: string;
  contactId: string;
  subject: string;
  description: string;
  nextAction: string;
  dueDate: string;
}

const OPTIONAL: ReadonlySet<keyof Values> = new Set([
  'contactId',
  'subject',
  'description',
  'nextAction',
  'dueDate',
]);

/**
 * Record (no `activity`) or edit one client activity (FRD-CRM-025 to CRM-030). The type comes from
 * the configurable activity types; the contact is one of the client's active contacts; the
 * responsible employee and the project are active records searched on the server. An activity
 * records a subject or a description. An edit sends only what changed, with If-Match.
 */
export function ActivityDialog({
  clientId,
  activity,
  onClose,
}: {
  clientId: string;
  activity?: ClientActivityDto;
  onClose: (saved?: ClientActivityDto) => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const lists = useCrmLists();
  const can = (capability: string) => hasCapability(session.data, capability);
  const creating = activity === undefined;
  const original: Values = {
    activityTypeCode: activity?.activityTypeCode ?? '',
    activityDate: activity?.activityDate ?? businessToday(session.data?.businessTimezone),
    contactId: activity?.contactId ?? '',
    subject: activity?.subject ?? '',
    description: activity?.description ?? '',
    nextAction: activity?.nextAction ?? '',
    dueDate: activity?.dueDate ?? '',
  };
  const [values, setValues] = useState<Values>(original);
  const [responsible, setResponsible] = useState<EmployeeDto | null>(null);
  const [project, setProject] = useState<ProjectDto | null>(null);
  const [clearProject, setClearProject] = useState(false);
  const set = (field: keyof Values) => (value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
  };

  // Only this client's active contacts are selectable (FRD-CRM-022, CRM-023).
  const contacts = useQuery({
    queryKey: ['crm', 'contacts', clientId, 'active'],
    enabled: can('crm.client_contact:view'),
    queryFn: () =>
      getPage(
        `${CLIENTS}/${clientId}/contacts?pageSize=200&filter[status]=active`,
        ClientContactSchema,
      ),
  });

  const body: Record<string, string | null> = {};
  for (const field of Object.keys(values) as (keyof Values)[]) {
    const value = values[field].trim();
    if (creating) {
      if (value !== '') body[field] = value;
    } else if (value !== original[field]) {
      body[field] = OPTIONAL.has(field) && value === '' ? null : value;
    }
  }
  if (responsible !== null && responsible.id !== activity?.responsibleEmployeeId) {
    body.responsibleEmployeeId = responsible.id;
  }
  if (project !== null && project.id !== activity?.projectId) body.projectId = project.id;
  if (!creating && clearProject && activity.projectId !== null) body.projectId = null;

  const hasText = values.subject.trim() !== '' || values.description.trim() !== '';
  const complete =
    values.activityTypeCode !== '' &&
    values.activityDate !== '' &&
    hasText &&
    (!creating || responsible !== null);

  const save = useMutation({
    mutationFn: () =>
      creating
        ? postJson(
            `${CLIENTS}/${clientId}/activities`,
            body,
            ClientActivitySchema,
            session.data?.csrfToken ?? '',
          )
        : patchJson(
            `${ACTIVITIES}/${activity.id}`,
            body,
            ClientActivitySchema,
            session.data?.csrfToken ?? '',
            activity.version,
          ),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['crm', 'activities'] });
      await queryClient.invalidateQueries({ queryKey: ['crm', 'activity'] });
      onClose(saved);
    },
  });

  const contactOptions = contacts.data?.items ?? [];
  return (
    <FormDialog
      open
      title={creating ? 'New activity' : 'Edit activity'}
      submitLabel={creating ? 'Record activity' : 'Save changes'}
      canSubmit={complete && Object.keys(body).length > 0}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={() => {
        onClose();
      }}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <ListSelect
        label="Activity type"
        required
        values={lists.data?.activityType}
        value={values.activityTypeCode}
        current={activity?.activityTypeCode}
        onChange={set('activityTypeCode')}
      />
      <TextField
        label="Activity date"
        type="date"
        required
        value={values.activityDate}
        onChange={(e) => {
          set('activityDate')(e.target.value);
        }}
        slotProps={{ inputLabel: { shrink: true } }}
      />
      {can('crm.client_contact:view') && (
        <TextField
          select
          label="Contact"
          value={values.contactId}
          onChange={(e) => {
            set('contactId')(e.target.value);
          }}
          helperText="Optional: one of the client's active contacts."
        >
          <MenuItem value="">None</MenuItem>
          {contactOptions.map((contact) => (
            <MenuItem key={contact.id} value={contact.id}>
              {contact.name}
              {contact.designation === null ? '' : ` — ${contact.designation}`}
            </MenuItem>
          ))}
          {/* A current contact since deactivated keeps its place on this record. */}
          {values.contactId !== '' && !contactOptions.some((c) => c.id === values.contactId) && (
            <MenuItem value={values.contactId}>Current contact (inactive or not visible)</MenuItem>
          )}
        </TextField>
      )}
      {can('hr.employee:view') ? (
        <ActiveEmployeePicker
          label={creating ? 'Responsible employee' : 'Change responsible employee'}
          required={creating}
          value={responsible}
          onChange={setResponsible}
          helperText={creating ? undefined : 'Optional'}
        />
      ) : (
        <Alert severity="warning" variant="outlined">
          Choosing the responsible employee needs permission to view employees.
        </Alert>
      )}
      <TextField
        label="Subject"
        value={values.subject}
        onChange={(e) => {
          set('subject')(e.target.value);
        }}
        helperText="A subject or a description is required."
      />
      <TextField
        label="Description"
        multiline
        minRows={2}
        value={values.description}
        onChange={(e) => {
          set('description')(e.target.value);
        }}
      />
      <TextField
        label="Next action"
        value={values.nextAction}
        onChange={(e) => {
          set('nextAction')(e.target.value);
        }}
      />
      <TextField
        label="Follow-up due date"
        type="date"
        value={values.dueDate}
        onChange={(e) => {
          set('dueDate')(e.target.value);
        }}
        slotProps={{ inputLabel: { shrink: true } }}
      />
      {can('projects.project:view') && (
        <>
          <ActiveProjectPicker
            label={activity?.projectId == null ? 'Project' : 'Change project'}
            value={project}
            onChange={(value) => {
              setProject(value);
              setClearProject(false);
            }}
            helperText={
              activity?.projectLabel == null ? 'Optional' : `Currently ${activity.projectLabel}`
            }
          />
          {!creating && activity.projectId !== null && (
            <TextField
              select
              label="Project link"
              value={clearProject ? 'clear' : 'keep'}
              onChange={(e) => {
                setClearProject(e.target.value === 'clear');
                if (e.target.value === 'clear') setProject(null);
              }}
            >
              <MenuItem value="keep">Keep the project</MenuItem>
              <MenuItem value="clear">Remove the project</MenuItem>
            </TextField>
          )}
        </>
      )}
    </FormDialog>
  );
}
